import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { withTimeout, processImageUrl } from "../utils/Timeout"; // Adjust the path as needed

export const fetchHomepage = createAsyncThunk(
  "homepage/fetchHomepage",
  async (forceRefresh = false, { rejectWithValue, getState }) => {
    try {
      // Check if we should skip the fetch (unless force refresh is true)
      if (!forceRefresh) {
        const { homepage } = getState();
        const { homepage: data, lastFetched } = homepage;
        
        // Skip if data exists and was fetched recently (within 5 minutes)
        if (data?.length > 0 && lastFetched && (Date.now() - lastFetched < 5 * 60 * 1000)) {
          return data; // Return cached data
        }
      }

      const response = await withTimeout(
        axios.get(
          `${process.env.REACT_APP_API_BASE_URL}rest/entities/Homepage?fetchPlan=homepage-fetch-plan&sort=id`
        ),
        10000
      );

      const processedData = response.data.map((item) => ({
        ...item,
        // Homepage sections are full-bleed banners, capped by the 1440px layout
        imageUrl: processImageUrl(item.imageUrl, 1440),
      }));

      return processedData;
    } catch (error) {
      return rejectWithValue(error?.response?.data || "Unknown error occurred");
    }
  }
);

const initialState = {
  homepage: [],
  loading: false,
  error: null,
  lastFetched: null, // Add timestamp tracking
};

const homepageSlice = createSlice({
  name: "homepage",
  initialState,
  reducers: {
    // Optional: Add manual refresh action
    refreshHomepage: (state) => {
      state.lastFetched = null; // This will trigger a fresh fetch
      state.error = null;
    },
    // Optional: Clear homepage data
    clearHomepage: (state) => {
      state.homepage = [];
      state.lastFetched = null;
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchHomepage.pending, (state) => {
        // console.log("Fetching homepage data (Pending)...");
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchHomepage.fulfilled, (state, action) => {
        // console.log("Homepage data fetched successfully:", action.payload);
        state.homepage = action.payload;
        state.loading = false;
        state.lastFetched = Date.now(); // Track when data was fetched
        state.error = null;
      })
      .addCase(fetchHomepage.rejected, (state, action) => {
        console.error("Homepage fetch failed:", action.payload);
        state.loading = false;
        state.error = action.payload;
        state.homepage = []; // Reset to empty array instead of null
        // Don't update lastFetched on error to allow retry
      });
  },
});

export const { refreshHomepage, clearHomepage } = homepageSlice.actions;
export default homepageSlice.reducer;